import React, { FunctionComponent } from 'react'
import { Link } from 'react-router-dom'
import useTranslation from '../../hooks/useTranslation'
import NavLink from '../../entities/NavLink'
import { getLoadingClassName } from '../loading-indicator-wrapper'
import CartboxPrices from '../cartbox-prices'
import RenderImagesPreview from '../render-images-preview'
import VehicleCodeButton from '../vehicle-code-button'
import CartboxEmissionConsumption from '../cartbox-emission-consumption'
import ResetConfigurationButton from '../reset-configuration-button'
import style from './cartbox.css'
import PdfButton from '../pdf-button'
import UserGarageButton from '../user-garage-button'
import DigitalGiveawayButton from '../digital-giveaway-button'
import { CartEmissionConsumptions } from '../../entities/CartEmissionConsumptions'
import CartboxRemarks from '../cartbox-remarks/CartboxRemarks'
import CartboxPriceViewSwitcher from '../cartbox-price-view-switcher'
import TechnicalDataButton from '../technical-data-button'
import CartboxE3DButtonContainer from '../cartbox-e3d-button'
import SaveConfigurationButton from '../save-configuration-button'

export type Props = {
    cartIsVisible: boolean
    cartName: string
    cartNavLink: NavLink
    isLoading: boolean
    isAudiExclusivePdfVisible: boolean
    hasAudiExclusiveEquipments: boolean
    isDigitalGiveawayLinkVisible: boolean
    isUserGarageButtonVisible: boolean
    cartAdditionalInformation: CartEmissionConsumptions
    cartRemarks: CartRemarks
    displayPriceViewSwitcher: boolean
    isTechnicalDataButtonVisible: boolean
    setE3DSharingVisible?: (visible: boolean) => void
    is3DExhibitionEnabled?: boolean
    isSaveVehicleCodeVisible?: boolean
    isSaveConfigurationButtonVisible?: boolean
    hasRecommendationsAbove?: boolean
}

const AUDI_EXCLUSIVE_SELECTION = 'audiExclusive'
const GENERIC_PDF_TYPE = 'pdf'
const AUDI_EXCLUSIVE_PDF_TYPE = 'audiExclusivePdf'

const Cartbox: FunctionComponent<Props> = (props) => {
    const {
        cartIsVisible,
        cartName,
        cartNavLink,
        isLoading,
        isAudiExclusivePdfVisible,
        hasAudiExclusiveEquipments,
        isDigitalGiveawayLinkVisible,
        isUserGarageButtonVisible,
        cartAdditionalInformation,
        cartRemarks,
        displayPriceViewSwitcher,
        isTechnicalDataButtonVisible,
        setE3DSharingVisible,
        is3DExhibitionEnabled,
        isSaveVehicleCodeVisible,
        isSaveConfigurationButtonVisible,
        hasRecommendationsAbove,
    } = props
    const { t, i18n } = useTranslation()

    if (!cartIsVisible || !cartNavLink) {
        return null
    }

    const priceSwitcher = displayPriceViewSwitcher ? <CartboxPriceViewSwitcher /> : null

    const e3dSharingButton = is3DExhibitionEnabled ? (
        <div className={style.e3dButtonContainer}>
            <CartboxE3DButtonContainer setE3DSharingVisible={setE3DSharingVisible} />
        </div>
    ) : null

    const buttonLabel = i18n.exists(`cartNavigation.${cartNavLink.id}`)
        ? t(`cartNavigation.${cartNavLink.id}`)
        : t('cartNavigation.goTo', { label: t(cartNavLink.navLabel) })

    return (
        <section className={`${style.container} ${getLoadingClassName(isLoading)} ${hasRecommendationsAbove ? style.noTopMargin : ''}`}>
            <h2 className={style.title} data-testid="cart-title">
                {cartName || t('loading')}
            </h2>
            <div className={style.imagesContainer}>
                <RenderImagesPreview />
            </div>
            {e3dSharingButton}
            <div className={style.pricesContainer}>
                <CartboxPrices />
            </div>
            {priceSwitcher}
            <div className={style.extraDetailsContainer}>
                <CartboxEmissionConsumption emissionConsumption={cartAdditionalInformation} />
                <CartboxRemarks remarks={cartRemarks} />
            </div>
            <div className={style.navButtonContainer}>
                <Link
                    to={cartNavLink.url}
                    className={style.navButton}
                    data-testid={`cart-button-continue-${cartNavLink.id.replace(/\\/g, '.')}`}>
                    {buttonLabel || t('loading')}
                </Link>
            </div>
            <ul className={style.linkList}>
                <li>
                    <ResetConfigurationButton />
                </li>
                <li>
                    <PdfButton pdfType={GENERIC_PDF_TYPE} excludeParts={[AUDI_EXCLUSIVE_SELECTION]} />
                </li>
                {isSaveVehicleCodeVisible && (
                    <li>
                        <VehicleCodeButton />
                    </li>
                )}
                {isSaveConfigurationButtonVisible && (
                    <li>
                        <SaveConfigurationButton />
                    </li>
                )}
                {isAudiExclusivePdfVisible && hasAudiExclusiveEquipments && (
                    <li>
                        <PdfButton pdfType={AUDI_EXCLUSIVE_PDF_TYPE} />
                    </li>
                )}
                {isDigitalGiveawayLinkVisible && (
                    <li>
                        <DigitalGiveawayButton />
                    </li>
                )}
                {isUserGarageButtonVisible && (
                    <li>
                        <UserGarageButton />
                    </li>
                )}
                {isTechnicalDataButtonVisible && (
                    <li>
                        <TechnicalDataButton />
                    </li>
                )}
            </ul>
        </section>
    )
}

export default Cartbox
